import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { isOrOnlyCombined } from "@/shared/utils/combinedFilters";
import { typedTermsWatchlists } from "./listStorage";
import type { ListContent } from "./builderModel";

/**
 * Combined watchlists picked into another watchlist.
 *
 * Every Newsquawk index list is a combined watchlist — a hidden list of its
 * tickers OR the tags that cover it — and people build on those. The builder
 * lets them tick one like any other watchlist and shows it as the one thing
 * they picked. Underneath nothing nests: saving copies its parts into the new
 * list, the way "Build on" copies a Newsquawk combination. Its watchlists are
 * linked by id, so a change to an index's constituents still flows through,
 * and its tags and keyword expression are copied.
 *
 * That is exact for a list whose operators are all OR, which every index list
 * and every concierge list is: a union merged into a union is the same union.
 * A list that uses AND can't be merged without changing what it matches, so
 * it can only be started from (a copy). content-hub's nested filters remove
 * that limit.
 *
 * Its own module, importing only types and pure helpers, so the rules can be
 * tested in node.
 */

/** The parts a combined watchlist is saved from. */
export interface CombinedParts {
  portfolioIds: number[];
  categories: string[];
  tagsWatchlistIds: number[];
  searchKeyword: string;
}

export const noParts = (): CombinedParts => ({
  portfolioIds: [],
  categories: [],
  tagsWatchlistIds: [],
  searchKeyword: "",
});

export function partsOf(query: CategoryQuery): CombinedParts {
  return {
    portfolioIds: query.portfolioIds ?? (query.portfolios ?? []).map((p) => p.id),
    categories: (query.categories ?? []).map(String),
    tagsWatchlistIds:
      query.tagsWatchlistIds ?? (query.tagsWatchlists ?? []).map((t) => t.id),
    searchKeyword: query.searchKeyword?.trim() ?? "",
  };
}

/** What a combined list holds, by box. */
export function contentOf(query: CategoryQuery): ListContent {
  const portfolios = query.portfolios ?? [];
  return {
    keywords:
      !!query.searchKeyword?.trim() ||
      portfolios.some((p) => (p.keywords?.length ?? 0) > 0),
    tickers: portfolios.some((p) => p.tickers.length > 0),
    tags:
      (query.categories?.length ?? 0) > 0 ||
      (query.tagsWatchlistIds?.length ?? query.tagsWatchlists?.length ?? 0) > 0,
  };
}

/** Whether a combined list can be included as it is. */
export function canInclude(query: CategoryQuery): boolean {
  return isOrOnlyCombined(query);
}

/**
 * A keyword expression's OR-groups. Web-search syntax has no brackets, so an
 * expression is AND-groups joined by OR, and joining two keeps every group
 * intact. An OR inside quotes is part of a phrase and stays.
 */
export function orGroups(expression: string): string[] {
  const groups: string[] = [];
  let current = "";
  let quoted = false;
  let i = 0;
  while (i < expression.length) {
    const ch = expression[i];
    if (ch === '"') quoted = !quoted;
    if (!quoted) {
      const or = /^\s+OR\s+/.exec(expression.slice(i));
      if (or) {
        groups.push(current);
        current = "";
        i += or[0].length;
        continue;
      }
    }
    current += ch;
    i++;
  }
  groups.push(current);
  return groups.map((g) => g.trim()).filter(Boolean);
}

/** Expressions OR-joined, each group once. */
export function joinExpressions(expressions: string[]): string {
  const filled = expressions.map((e) => e.trim()).filter(Boolean);
  if (filled.length <= 1) return filled[0] ?? "";
  const groups: string[] = [];
  for (const expression of filled) {
    for (const group of orGroups(expression)) {
      if (!groups.includes(group)) groups.push(group);
    }
  }
  return groups.join(" OR ");
}

const unique = <T,>(items: T[]): T[] => [...new Set(items)];

/** The builder's own parts with the included lists' copied in. */
export function withIncluded(
  own: CombinedParts,
  included: CategoryQuery[]
): CombinedParts {
  const all = [own, ...included.map(partsOf)];
  return {
    portfolioIds: unique(all.flatMap((p) => p.portfolioIds)),
    categories: unique(all.flatMap((p) => p.categories)),
    tagsWatchlistIds: unique(all.flatMap((p) => p.tagsWatchlistIds)),
    searchKeyword: joinExpressions(all.map((p) => p.searchKeyword)),
  };
}

function partCount(parts: CombinedParts): number {
  return (
    parts.portfolioIds.length +
    parts.categories.length +
    parts.tagsWatchlistIds.length +
    orGroups(parts.searchKeyword).length
  );
}

export interface Recognised {
  /** Combined lists the loaded one includes, to show as one chip each. */
  includedIds: number[];
  /** What's left: the builder's own picks. */
  rest: CombinedParts;
}

/**
 * Finds the combined lists a saved list includes, so reopening it shows the
 * S&P 500 the user ticked rather than a hidden list of 444 tickers and a tag.
 *
 * `loaded` is the list's parts without its own typed-terms lists.
 *
 * - A list with hidden lists of its own (an index list, or one of the
 *   user's with typed terms) is recognised by those alone: nobody picks a
 *   hidden list by hand, so holding them all means it was included. What it
 *   has gained since (a tag the desk added) is merged in again on save.
 * - A list without is recognised only when all of its parts are there and
 *   it has two or more, since a single tag or watchlist is as likely to
 *   have been picked on its own.
 *
 * Otherwise its parts simply show one by one, and still match the same.
 * `allQueries` lets each candidate's own hidden lists be told from lists it
 * includes in turn (typedTermsWatchlists).
 */
export function recogniseIncluded(
  loaded: CombinedParts,
  candidates: CategoryQuery[],
  allQueries: CategoryQuery[]
): Recognised {
  const has = {
    portfolioIds: new Set(loaded.portfolioIds),
    categories: new Set(loaded.categories),
    tagsWatchlistIds: new Set(loaded.tagsWatchlistIds),
    groups: new Set(orGroups(loaded.searchKeyword)),
  };
  const holdsAll = (parts: CombinedParts) =>
    parts.portfolioIds.every((id) => has.portfolioIds.has(id)) &&
    parts.categories.every((id) => has.categories.has(id)) &&
    parts.tagsWatchlistIds.every((id) => has.tagsWatchlistIds.has(id)) &&
    orGroups(parts.searchKeyword).every((g) => has.groups.has(g));

  const strong: CategoryQuery[] = [];
  const weak: { query: CategoryQuery; size: number }[] = [];
  for (const query of candidates) {
    if (query.id == null || !canInclude(query)) continue;
    const own = typedTermsWatchlists(query, allQueries).map((p) => p.id);
    if (own.length > 0) {
      if (own.every((id) => has.portfolioIds.has(id))) strong.push(query);
      continue;
    }
    const parts = partsOf(query);
    const size = partCount(parts);
    if (size >= 2 && holdsAll(parts)) weak.push({ query, size });
  }
  weak.sort((a, b) => b.size - a.size);

  const claimed = {
    portfolioIds: new Set<number>(),
    categories: new Set<string>(),
    tagsWatchlistIds: new Set<number>(),
    groups: new Set<string>(),
  };
  const includedIds: number[] = [];
  const claim = (query: CategoryQuery, mustAddSomething: boolean) => {
    const parts = partsOf(query);
    const ids = parts.portfolioIds.filter((id) => has.portfolioIds.has(id));
    const tags = parts.categories.filter((id) => has.categories.has(id));
    const lists = parts.tagsWatchlistIds.filter((id) => has.tagsWatchlistIds.has(id));
    const groups = orGroups(parts.searchKeyword).filter((g) => has.groups.has(g));
    const adds =
      ids.some((id) => !claimed.portfolioIds.has(id)) ||
      tags.some((id) => !claimed.categories.has(id)) ||
      lists.some((id) => !claimed.tagsWatchlistIds.has(id)) ||
      groups.some((g) => !claimed.groups.has(g));
    if (mustAddSomething && !adds) return;
    ids.forEach((id) => claimed.portfolioIds.add(id));
    tags.forEach((id) => claimed.categories.add(id));
    lists.forEach((id) => claimed.tagsWatchlistIds.add(id));
    groups.forEach((g) => claimed.groups.add(g));
    includedIds.push(query.id as number);
  };
  strong.forEach((query) => claim(query, false));
  weak.forEach(({ query }) => claim(query, true));

  if (includedIds.length === 0) return { includedIds, rest: loaded };
  const restGroups = orGroups(loaded.searchKeyword).filter((g) => !claimed.groups.has(g));
  return {
    includedIds,
    rest: {
      portfolioIds: loaded.portfolioIds.filter((id) => !claimed.portfolioIds.has(id)),
      categories: loaded.categories.filter((id) => !claimed.categories.has(id)),
      tagsWatchlistIds: loaded.tagsWatchlistIds.filter(
        (id) => !claimed.tagsWatchlistIds.has(id)
      ),
      searchKeyword:
        claimed.groups.size === 0 ? loaded.searchKeyword : restGroups.join(" OR "),
    },
  };
}
